import React, { useEffect, useMemo, useRef, useState } from 'react';

type LazyLoadedImageProps = {
  src: string | undefined;
  alt: string | undefined;
  className?: string;
  enabled?: boolean;
  offset?: number;
  onLoad?: React.ReactEventHandler<HTMLImageElement>;
};

function setRef<T>(ref: React.ForwardedRef<T>, value: T | null): void {
  if (typeof ref === 'function') {
    ref(value);
  } else if (ref && 'current' in ref) {
    (ref as React.MutableRefObject<T | null>).current = value;
  }
}

const LazyLoadedImage = React.forwardRef<HTMLImageElement, LazyLoadedImageProps>(function LazyLoadedImage(
  { src, alt, className, enabled = true, offset = 400, onLoad }: LazyLoadedImageProps,
  forwardedRef: React.ForwardedRef<HTMLImageElement>
): React.ReactElement | null {
  const innerRef = useRef<HTMLImageElement | null>(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  const combinedRef = useMemo(() => {
    return (node: HTMLImageElement | null): void => {
      innerRef.current = node;
      setRef(forwardedRef, node);
    };
  }, [forwardedRef]);

  useEffect((): void | (() => void) => {
    if (!enabled || !src) {
      setShouldLoad(false);

      return;
    }

    if (shouldLoad) return;

    const el = innerRef.current;

    if (!el || typeof IntersectionObserver === 'undefined') {
      setShouldLoad(true);

      return;
    }

    const m = Math.max(0, offset);

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShouldLoad(true);
            io.disconnect();
            break;
          }
        }
      },
      {
        root: null,
        rootMargin: `${m}px 0px ${m}px 0px`,
        threshold: 0.01,
      }
    );

    io.observe(el);

    return (): void => {
      io.disconnect();
    };
  }, [enabled, src, offset, shouldLoad]);

  if (!enabled) return null;

  return (
    <img
      ref={combinedRef}
      alt={alt}
      className={className}
      src={shouldLoad ? src : undefined}
      data-src={!shouldLoad ? src : undefined}
      loading="lazy"
      decoding="async"
      onLoad={onLoad}
    />
  );
});

export default LazyLoadedImage;
